// Redux/reducers/deliveryReducer.js
const initialState = {
    deliveries: [],
    currentDelivery: null,
    loading: false,
    /** True after boot/Sync SET_DELIVERIES — empty-by-order is known-empty, not a miss. */
    listHydrated: false,
};

// Load from localStorage on init
const loadDeliveriesFromStorage = () => {
    const stored = localStorage.getItem('hpos_deliveries');
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch (e) {
            return [];
        }
    }
    return [];
};

// Save to localStorage helper
const saveDeliveriesToStorage = (deliveries) => {
    localStorage.setItem('hpos_deliveries', JSON.stringify(deliveries));
};

// Generate unique ID
const generateId = () => {
    return `DN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Generate delivery number
const generateDeliveryNumber = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `DN-${year}${month}-${random}`;
};

export const DELIVERY_STATUS = {
    PENDING: "PENDING",
    READY: "READY",
    SHIPPED: "SHIPPED",
    DELIVERED: "DELIVERED",
    CANCELLED: "CANCELLED",
    PARTIAL: "PARTIAL"
};

const hasDeliveryItems = (d) => {
    const items = d?.items || d?.deliveryLineItems || d?.lineItems;
    return Array.isArray(items) && items.length > 0;
};

/** Prefer richer items / detail-fetched stamp so Sync/list replace don't wipe them. */
const mergeDeliveryPreserveItems = (existing, incoming) => {
    if (!incoming) return existing || null;
    if (!existing) return incoming;
    const existingItems =
        existing.items || existing.deliveryLineItems || existing.lineItems || [];
    const incomingItems =
        incoming.items || incoming.deliveryLineItems || incoming.lineItems || [];
    const keepItems =
        !hasDeliveryItems(incoming) && hasDeliveryItems(existing)
            ? existingItems
            : incomingItems;
    const keepFetched = Boolean(existing.__detailFetched || incoming.__detailFetched);
    return {
        ...existing,
        ...incoming,
        items: keepItems,
        deliveryLineItems: keepItems,
        lineItems: keepItems,
        ...(keepFetched ? { __detailFetched: true } : {}),
    };
};

export const deliveryReducer = (state = { ...initialState, deliveries: loadDeliveriesFromStorage() }, action) => {
    let newDeliveries;

    switch (action.type) {
        case "DELIVERIES_LOADING":
            return { ...state, loading: action.payload };

        case "GET_DELIVERIES":
            return { ...state, deliveries: loadDeliveriesFromStorage(), loading: false };

        case "SET_DELIVERIES": {
            const incoming = Array.isArray(action.payload) ? action.payload : [];
            const prevById = new Map(
                (state.deliveries || []).map((d) => [String(d.POID || d.id || ""), d])
            );
            const deliveries = incoming.map((d) => {
                const id = String(d?.POID || d?.id || "");
                if (!id) return d;
                return mergeDeliveryPreserveItems(prevById.get(id), d);
            });
            return {
                ...state,
                deliveries,
                listHydrated: true,
                loading: false,
            };
        }

        case "UPSERT_DELIVERY": {
            const incoming = action.payload;
            if (!incoming) return state;
            const incomingId = String(incoming.POID || incoming.id || "");
            if (!incomingId || incomingId === "null") return state;
            const existing = (state.deliveries || []).find(
                (d) => String(d.POID || d.id) === incomingId
            );
            const merged = mergeDeliveryPreserveItems(existing, incoming);
            const deliveries = existing
                ? (state.deliveries || []).map((d) =>
                    String(d.POID || d.id) === incomingId ? merged : d
                )
                : [merged, ...(state.deliveries || [])];
            return { ...state, deliveries, loading: false };
        }

        case "GET_DELIVERY": {
            const delivery = loadDeliveriesFromStorage().find(d => (d.POID || d.id) === action.payload);
            return { ...state, currentDelivery: delivery, loading: false };
        }

        case "CREATE_DELIVERY": {
            const newDelivery = {
                ...action.payload,
                POID: generateId(),
                id: generateId(),
                deliveryNumber: generateDeliveryNumber(),
                status: DELIVERY_STATUS.PENDING,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            newDeliveries = [newDelivery, ...loadDeliveriesFromStorage()];
            saveDeliveriesToStorage(newDeliveries);
            return { ...state, deliveries: newDeliveries, currentDelivery: newDelivery, loading: false };
        }

        case "UPDATE_DELIVERY": {
            const existingDeliveries = loadDeliveriesFromStorage();
            newDeliveries = existingDeliveries.map(d =>
                (d.POID === action.payload.id || d.id === action.payload.id)
                    ? { ...d, ...action.payload.data, updatedAt: new Date().toISOString() }
                    : d
            );
            saveDeliveriesToStorage(newDeliveries);
            return { ...state, deliveries: newDeliveries, loading: false };
        }

        case "UPDATE_DELIVERY_STATUS": {
            const existingDeliveries = loadDeliveriesFromStorage();
            newDeliveries = existingDeliveries.map(d =>
                (d.POID === action.payload.id || d.id === action.payload.id)
                    ? {
                        ...d,
                        status: action.payload.status,
                        trackingNumber: action.payload.trackingNumber || d.trackingNumber,
                        shippedAt: action.payload.status === DELIVERY_STATUS.SHIPPED ? new Date().toISOString() : d.shippedAt,
                        deliveredAt: action.payload.status === DELIVERY_STATUS.DELIVERED ? new Date().toISOString() : d.deliveredAt,
                        updatedAt: new Date().toISOString()
                    }
                    : d
            );
            saveDeliveriesToStorage(newDeliveries);
            return { ...state, deliveries: newDeliveries, loading: false };
        }

        case "DELETE_DELIVERY": {
            const existingDeliveries = loadDeliveriesFromStorage();
            newDeliveries = existingDeliveries.filter(d => (d.POID !== action.payload && d.id !== action.payload));
            saveDeliveriesToStorage(newDeliveries);
            return { ...state, deliveries: newDeliveries, loading: false };
        }

        default:
            return state;
    }
};